import * as uniqBy from 'lodash/uniqBy'

import {
  ABOUT_URL,
  ASSETS_URL,
  CREDIT_LINKAGE_URL,
  CREDIT_URL,
  DASHBOARD_URL,
  DEBTS_URL,
  FAQ_URL,
  HOMEPAGE_URL,
  INCOME_URL,
  LOGIN_URL,
  LOGOUT_URL,
  NETWORTH_URL,
  OFFERS_URL,
  ONBOARDING_PROPERTY_URL,
  ONBOARDING_REGISTER_URL,
  ONBOARDING_SUMMARY_URL,
  OPPORTUNITIES_URL,
  PASSWORD_RESET_URL,
  PRESS_URL,
  PRIVACY_POLICY_URL,
  REDEMPTION_ENTRY_URL,
  REWARDS_URL,
  SECURITY_URL,
  SETTINGS_ACCOUNTS_URL,
  SETTINGS_URL,
  SG_ATOMS_URL,
  SG_META_URL,
  SG_MOLECULES_URL,
  SG_ORGANISMS_URL,
  SG_THEMES_URL,
  SG_WORKSPACE_URL,
  SPENDING_URL,
  STYLEGUIDE_URL,
  TERMS_CONDITIONS_URL,
  FEED_POSTS_URL,
  FEED_POSTS_BASE_URL,
  FEED_AUTHORS_BASE_URL,
  FEED_TAGS_BASE_URL,
  FEED_POST_URL,
  FEED_AUTHOR_URL,
  FEED_TAG_URL,
  INTERNAL_ENTRY_URL,
  LINKED_ACCOUNTS_URL,
  TRANSACTIONS_URL,
  EXTERNAL_DASHBOARD_URL,
  FUTURE_URL,
  COACH_URL,
  VIDEO_MEETING_URL,
  COACH_REVIEW_URL,
  CREDIT_CARD_URL,
} from 'tangle-shared'
import {
  UncategorizedPageType,
  CreditLinkagePageType,
  ExternalPageType,
  InternalMenuNavPageType,
  InternalOnboardingPage,
  InternalPageType,
  LogoutPageType,
  MenuNavPageType,
  OfferPageType,
  OpportunitiesPageType,
  PageType,
  RedemptionPageType,
  SettingsAccountsPageType,
  StyleguidePageType,
  TimeframedPage,
  FullAccountPage,
  FullMoneyflowPage,
  AccountPage,
  FuturePageType,
  VideoMeetingPageType,
  CoachReviewPageType,
  CreditCardLandingPageType,
} from 'interfaces/pages'
import { getRedemptionFormUrl } from 'helpers/bountyHelpers'
import { REDEMPTION_AMOUNT_STEP_NUMBER } from 'constants/bounty'

// Onboarding Pages
export const ONBOARDING_REGISTER = 'onboarding-register' as const
export const ONBOARDING_PROPERTY = 'onboarding-property' as const
export const ONBOARDING_SUMMARY = 'onboarding-summary' as const

// Internal Pages
export const FEED_POSTS_PAGE = 'feedposts' as const
export const FEED_POST_PAGE = 'feedpost' as const
export const FEED_AUTHOR_PAGE = 'feedauthor' as const
export const FEED_TAG_PAGE = 'feedtag' as const
export const DASHBOARD_PAGE = 'dashboard' as const
export const TRANSACTIONS_PAGE = 'transactions' as const
export const INCOME_PAGE = 'income' as const
export const SPENDING_PAGE = 'spending' as const
export const LINKED_ACCOUNTS_PAGE = 'linkedaccounts' as const
export const DEBTS_PAGE = 'debts' as const
export const ASSETS_PAGE = 'assets' as const
export const NETWORTH_PAGE = 'networth' as const
export const CREDIT_PAGE = 'creditscore' as const
export const SETTINGS_PAGE = 'settings' as const
export const SETTINGS_ACCOUNTS_PAGE = 'settings-accounts' as const
export const CREDIT_LINKAGE_PAGE = 'credit-linkage' as const
export const OFFER_PAGE = 'offer' as const
export const OPPORTUNITIES_PAGE = 'opportunities' as const
export const REWARDS_PAGE = 'rewards' as const
export const REDEMPTION_PAGE = 'redemption' as const
export const FUTURE_PAGE = 'future' as const

// External Pages
export const HOME_PAGE = 'home' as const
export const LOGIN_PAGE = 'login' as const
export const PASSWORD_RESET_PAGE = 'password-reset' as const
export const ABOUT_PAGE = 'about' as const
export const PRESS_PAGE = 'press' as const
export const FAQ_PAGE = 'faq' as const
export const CARD_TERMS_PAGE = 'credit-card-terms' as const
export const TERMS_PAGE = 'terms' as const
export const PRIVACY_PAGE = 'privacy' as const
export const SECURITY_PAGE = 'security' as const
export const LOGOUT_PAGE = 'logout' as const
export const PUBLIC_DASHBOARD_PAGE = 'publicdashboard' as const

// Styleguide Pages
export const SG_INDEX_PAGE = 'styleguide' as const
export const SG_META_PAGE = 'meta' as const
export const SG_ATOMS_PAGE = 'atoms' as const
export const SG_MOLECULES_PAGE = 'molecules' as const
export const SG_ORGANISMS_PAGE = 'organisms' as const
export const SG_THEMES_PAGE = 'themes' as const
export const WORKSPACE_PAGE = 'workspace' as const

// Advisors
export const COACH_PAGE = 'coaching' as const
export const COACH_REVIEW_PAGE = 'coachreview' as const
export const VIDEO_MEETING_PAGE = 'video' as const

// Credit card page
export const CREDIT_CARD_LANDING_PAGE = 'card' as const
export const CREDIT_CARD_PAGE = 'creditcard' as const


export type PageDefinition<T> = {
  name: T
  title: string
  url: string
}

const LOGOUT_PAGE_DEFINITION: PageDefinition<LogoutPageType> = {
  name: 'logout',
  title: 'Logout',
  url: LOGOUT_URL,
}

const OFFER_PAGE_DEFINITION: PageDefinition<OfferPageType> = {
  name: 'offer',
  title: 'Offer',
  url: OFFERS_URL,
}

const OPPORTUNITIES_PAGE_DEFINITION: PageDefinition<OpportunitiesPageType> = {
  name: 'opportunities',
  title: 'Opportunities',
  url: OPPORTUNITIES_URL,
}

const FUTURE_PAGE_DEFINITION: PageDefinition<FuturePageType> = {
  name: 'future',
  title: 'Future',
  url: FUTURE_URL,
}

const REDEMPTION_PAGE_DEFINITION: PageDefinition<RedemptionPageType> = {
  name: 'redemption',
  title: 'Redemption',
  url: getRedemptionFormUrl(REDEMPTION_AMOUNT_STEP_NUMBER),
}

const CREDIT_LINKAGE_PAGE_DEFINITION: PageDefinition<CreditLinkagePageType> = {
  name: 'credit-linkage',
  title: 'Credit Linkage',
  url: CREDIT_LINKAGE_URL,
}

const SETTINGS_ACCOUNT_PAGE_DEFINITION: PageDefinition<SettingsAccountsPageType> = {
  name: 'settings-accounts',
  title: 'Settings Accounts',
  url: SETTINGS_ACCOUNTS_URL,
}

const VIDEO_MEETING_PAGE_DEFINITION: PageDefinition<VideoMeetingPageType> = {
  name: VIDEO_MEETING_PAGE,
  title: 'Video',
  url: VIDEO_MEETING_URL,
}

const COACH_REVIEW_PAGE_DEFINITION: PageDefinition<CoachReviewPageType> = {
  name: COACH_REVIEW_PAGE,
  title: 'Video',
  url: COACH_REVIEW_URL,
}

const CREDIT_CARD_PAGE_DEFINITION: PageDefinition<CreditCardLandingPageType> = {
  name: CREDIT_CARD_LANDING_PAGE,
  title: 'Credit card',
  url: CREDIT_CARD_URL,
}

export const INTERNAL_FEED_PAGE_DEFINITIONS: PageDefinition<UncategorizedPageType>[] = [
  { name: 'feedpost', title: 'Advice Feed', url: FEED_POSTS_BASE_URL },
  { name: 'feedauthor', title: 'Advice Feed', url: FEED_AUTHORS_BASE_URL },
  { name: 'feedtag', title: 'Advice Feed', url: FEED_TAGS_BASE_URL },
]

export const INTERNAL_MENU_NAV_PAGES_DEFINITION: PageDefinition<InternalMenuNavPageType>[] = [
  { name: 'feedposts', title: 'Advice Feed', url: FEED_POSTS_URL },
  { name: 'dashboard', title: 'Your Money', url: DASHBOARD_URL },
  { name: 'transactions', title: 'Transactions', url: TRANSACTIONS_URL },
  { name: 'spending', title: 'Spending', url: SPENDING_URL },
  { name: 'income', title: 'Income', url: INCOME_URL },
  { name: 'linkedaccounts', title: 'Linked Accounts', url: LINKED_ACCOUNTS_URL },
  { name: 'assets', title: 'Assets', url: ASSETS_URL },
  { name: 'debts', title: 'Debts', url: DEBTS_URL },
  { name: 'networth', title: 'Net Worth', url: NETWORTH_URL },
  { name: 'creditscore', title: 'Credit Profile', url: CREDIT_URL },
  { name: 'creditcard', title: 'Credit Card', url: '/credit-card' },
  { name: 'future', title: 'Future Plan', url: FUTURE_URL },
  { name: 'opportunities', title: 'Opportunities', url: OPPORTUNITIES_URL },
  { name: 'rewards', title: 'Rewards', url: REWARDS_URL },
  { name: 'settings', title: 'Settings', url: SETTINGS_URL },
  { name: COACH_PAGE, title: 'Coaching', url: COACH_URL },
]

export const MENU_NAV_PAGE_DEFINITIONS: PageDefinition<MenuNavPageType>[] = [
  ...INTERNAL_MENU_NAV_PAGES_DEFINITION,
  LOGOUT_PAGE_DEFINITION,
]

export const INTERNAL_ONBOARDING_PAGE_DEFINITIONS: PageDefinition<InternalOnboardingPage>[] = [
  { name: 'onboarding-property', title: 'Onboarding Property', url: ONBOARDING_PROPERTY_URL },
  { name: 'onboarding-summary', title: 'Onboarding Summary', url: ONBOARDING_SUMMARY_URL },
]

export const INTERNAL_PAGE_DEFINITIONS: PageDefinition<InternalPageType>[] = [
  ...INTERNAL_FEED_PAGE_DEFINITIONS,
  ...INTERNAL_MENU_NAV_PAGES_DEFINITION,
  ...INTERNAL_ONBOARDING_PAGE_DEFINITIONS,
  CREDIT_LINKAGE_PAGE_DEFINITION,
  SETTINGS_ACCOUNT_PAGE_DEFINITION,
  OFFER_PAGE_DEFINITION,
  OPPORTUNITIES_PAGE_DEFINITION,
  REDEMPTION_PAGE_DEFINITION,
  FUTURE_PAGE_DEFINITION,
  VIDEO_MEETING_PAGE_DEFINITION,
  COACH_REVIEW_PAGE_DEFINITION,
]

export const EXTERNAL_PAGE_DEFINITIONS: PageDefinition<ExternalPageType>[] = [
  { name: 'home', title: 'Home', url: HOMEPAGE_URL },
  { name: 'login', title: 'Login', url: LOGIN_URL },
  { name: 'password-reset', title: 'Password Reset', url: PASSWORD_RESET_URL },
  { name: 'about', title: 'About Us', url: ABOUT_URL },
  { name: 'press', title: 'Press', url: PRESS_URL },
  { name: 'faq', title: 'FAQ', url: FAQ_URL },
  { name: 'terms', title: 'Terms & Conditions', url: TERMS_CONDITIONS_URL },
  { name: 'privacy', title: 'Privacy Policy', url: PRIVACY_POLICY_URL },
  { name: 'security', title: 'Security', url: SECURITY_URL },
  { name: 'onboarding-register', title: 'Onboarding Register', url: ONBOARDING_REGISTER_URL },
  { name: 'publicdashboard', title: 'COVID-19', url: EXTERNAL_DASHBOARD_URL },
  LOGOUT_PAGE_DEFINITION,
  CREDIT_CARD_PAGE_DEFINITION,
]

export const STYLEGUIDE_PAGE_DEFINITIONS: PageDefinition<StyleguidePageType>[] = [
  { name: 'styleguide', title: 'Style Guide', url: STYLEGUIDE_URL },
  { name: 'meta', title: 'Meta', url: SG_META_URL },
  { name: 'atoms', title: 'Atoms', url: SG_ATOMS_URL },
  { name: 'molecules', title: 'Molecules', url: SG_MOLECULES_URL },
  { name: 'organisms', title: 'Organisms', url: SG_ORGANISMS_URL },
  { name: 'themes', title: 'Themes', url: SG_THEMES_URL },
  { name: 'workspace', title: 'Workspace', url: SG_WORKSPACE_URL },
]

export const PAGE_DEFINITIONS = uniqBy([
  ...INTERNAL_PAGE_DEFINITIONS,
  ...EXTERNAL_PAGE_DEFINITIONS,
  ...STYLEGUIDE_PAGE_DEFINITIONS,
], ({ name }) => name)

// means page where member should be authenticated
export const INTERNAL_PAGE_URLS_SET = new Set([
  FEED_AUTHOR_URL,
  FEED_TAG_URL,
  INTERNAL_ENTRY_URL,
  DASHBOARD_URL,
  SPENDING_URL,
  INCOME_URL,
  DEBTS_URL,
  ASSETS_URL,
  NETWORTH_URL,
  CREDIT_URL,
  REWARDS_URL,
  OPPORTUNITIES_URL,
  TRANSACTIONS_URL,
  LINKED_ACCOUNTS_URL,
  SETTINGS_URL,
  CREDIT_LINKAGE_URL,
  SETTINGS_ACCOUNTS_URL,
  ONBOARDING_PROPERTY_URL,
  ONBOARDING_SUMMARY_URL,
  COACH_URL,
])

export const INTERNAL_PAGE_FIRST_LEVEL_URLS = [
  OFFERS_URL,
  REDEMPTION_ENTRY_URL,
  FEED_POST_URL,
  FEED_AUTHOR_URL,
  FEED_TAG_URL,
]

export const EXTERNAL_PAGE_URLS_SET = new Set([
  HOMEPAGE_URL,
  EXTERNAL_DASHBOARD_URL,
  LOGIN_URL,
  PASSWORD_RESET_URL,
  ABOUT_URL,
  PRESS_URL,
  FAQ_URL,
  TERMS_CONDITIONS_URL,
  PRIVACY_POLICY_URL,
  SECURITY_URL,
  ONBOARDING_REGISTER_URL,
  LOGOUT_URL,
])

export const PREMIUM_PAGE_SET: Set<PageType> = new Set([
  <const>'spending',
  <const>'income',
  <const>'debts',
  <const>'assets',
  <const>'networth',
  <const>'creditscore',
])

export const PAGE_DEFINITIONS_MAP = PAGE_DEFINITIONS.reduce(
  (result, definition) => ({ ...result, [definition.name]: definition }),
  {} as Record<PageType, PageDefinition<PageType>>
)

export const URL_PAGE_MAP: Record<string, PageType> =
  PAGE_DEFINITIONS.reduce(
    (result, { name, url }) => ({ ...result, [url]: name }),
    {}
  )

export const PAGES_WITH_MAX_WIDTH = [
  LINKED_ACCOUNTS_URL,
  TRANSACTIONS_URL,
  SPENDING_URL,
  INCOME_URL,
  DEBTS_URL,
  ASSETS_URL,
  CREDIT_URL,
  NETWORTH_URL,
]

export const PAGES_WITH_LIVE_UPDATE = [
  DASHBOARD_URL,
  INCOME_URL,
  SPENDING_URL,
  DEBTS_URL,
  ASSETS_URL,
  NETWORTH_URL,
  CREDIT_URL,
  TRANSACTIONS_URL,
  LINKED_ACCOUNTS_URL,
  SETTINGS_URL,
]

export const PAGES_WITHOUT_COMPARISONS = ['linked-accounts', 'transactions', 'opportunities', 'rewards', 'settings', 'future', 'coaching', CREDIT_CARD_PAGE]
export const TIMEFRAMED_PAGES: TimeframedPage[] = ['income', 'spending', 'debts', 'assets', 'networth', 'creditscore', 'transactions', 'future']
export const MONEYFLOW_PAGES: FullMoneyflowPage[] = ['income', 'spending']
export const FULL_MONEYFLOW_PAGES: FullMoneyflowPage[] = [...MONEYFLOW_PAGES, 'transactions']
export const ACCOUNT_PAGES: AccountPage[] = ['debts', 'assets']
export const FULL_ACCOUNT_PAGES: FullAccountPage[] = [...ACCOUNT_PAGES, 'linkedaccounts']
export const CATEGORIZED_PAGES = [
  ...FULL_MONEYFLOW_PAGES,
  ...ACCOUNT_PAGES,
]

export const RETURN_URL_PARAM_NAME = 'returnUrl'
